import React, { useEffect, useMemo, useState } from 'react'
import * as PhosphorIcons from '@phosphor-icons/react'
import { copyToClipboard, formatDateTime, toast } from '../utils'
import ModalDeleteContentResult from './ModalDeleteContentResult'
import consumer from '../channels/consumer'
import CategoryViewResult from './results/CategoryViewResult'
import TagsViewResult from './results/TagsViewResult'
import SentimentViewResult from './results/SentimentViewResult'
import TextSummarizerViewResult from './results/TextSummarizerViewResult'
import AnomalyDetectorViewResult from './results/AnomalyDetectorViewResult'
import EntityRecognizerViewResult from './results/EntityRecognizerViewResult'
import MetadataExtractorViewResult from './results/MetadataExtractorViewResult'
import ActionAnalyzerViewResult from './results/ActionAnalyzerViewResult'
import DateExtractorViewResult from './results/DateExtractorViewResult'
import PiiDetectorViewResult from './results/PiiDetectorViewResult'
import DuplicateDetectorViewResult from './results/DuplicateDetectorViewResult'
import SpamAnalyzerViewResult from './results/SpamAnalyzerViewResult'
import EmotionAnalyzerViewResult from './results/EmotionAnalyzerViewResult'
import KpiAnalyzerViewResult from './results/KpiAnalyzerViewResult'
import UrgencyAnalyzerViewResult from './results/UrgencyAnalyzerViewResult'
import DeadlineAnalyzerViewResult from './results/DeadlineAnalyzerViewResult'
import KeywordOptimizerViewResult from './results/KeywordOptimizerViewResult'
import SeoAnalyzerViewResult from './results/SeoAnalyzerViewResult'
import ToneAnalyzerViewResult from './results/ToneAnalyzerViewResult'
import ReadabilityAnalyzerViewResult from './results/ReadabilityAnalyzerViewResult'
import ContentStructureViewResult from './results/ContentStructureViewResult'
import BiasDetectorViewResult from './results/BiasDetectorViewResult'
import FaqValidatorViewResult from './results/FaqValidatorViewResult'
import InvoiceExtractorViewResult from './results/InvoiceExtractorViewResult'
import IntentAnalyzerViewResult from './results/IntentAnalyzerViewResult'
import CitationAnalyzerViewResult from './results/CitationAnalyzerViewResult'
import ContractAnalysisViewResult from './results/ContractAnalysisViewResult'
import TransactionAnalyzerViewResult from './results/TransactionAnalyzerViewResult'
import LabResultAnalyzerViewResult from './results/LabResultAnalyzerViewResult'
import MedicalTermAnalyzerViewResult from './results/MedicalTermAnalyzerViewResult'
import ProductCategorizerViewResult from './results/ProductCategorizerViewResult'
import JobDescriptionAnalyzerViewResult from './results/JobDescriptionAnalyzerViewResult'
import MarketingMessageAnalyzerViewResult from './results/MarketingMessageAnalyzerViewResult'
import ExpirationCheckerViewResult from './results/ExpirationCheckerViewResult'
import TaxCodeIdentifierViewResult from './results/TaxCodeIdentifierViewResult'
import SafetyDataSheetViewResult from './results/SdsIntelligenceSystemResult'
import EnvironmentalSdsSystemResult from './results/EnvironmentalSdsSystemResult'
import FoodSpecificationViewResult from './results/FoodSpecificationViewResult'
import LanguageDetectorViewResult from './results/LanguageDetectorViewResult'
import FoodComparisonViewResult from './results/FoodComparisonViewResult'
import YAML from 'yaml'

const statusFiltersConfig = [
  {
    id: 'pending',
    label: 'Pending',
    icon: 'Clock',
    color_class: 'text-warning',
    bg_class: 'bg-warning',
  },
  {
    id: 'error',
    label: 'Error',
    icon: 'XCircle',
    color_class: 'text-error',
    bg_class: 'bg-error',
  },
  {
    id: 'success',
    label: 'Success',
    icon: 'CheckCircle',
    color_class: 'text-success',
    bg_class: 'bg-success',
  },
]

const AnalysisDashboard = ({
  project,
  content,
  agents,
  listOfAgents,
  initialContentResults,
}) => {
  const [state, setState] = useState({
    activeTab: 'results',
    selectedAgents: [],
    statusFilters: [],
    searchQuery: '',
  })
  const [contentResult, setContentResult] = useState(null)
  const [open, setOpen] = useState(false)
  const [contentResults, setContentResults] = useState(initialContentResults)

  useEffect(() => {
    const subscription = consumer.subscriptions.create('ContentResultChannel', {
      received (data) {
        if (data.type === 'content_result') {
          const newContentResult = data.result
          setContentResults(prev => {
            // Check if the result already exists in the list
            const existingIndex = prev.findIndex(
              result => result.id === newContentResult.id)

            if (existingIndex === -1) {
              // If it doesn't exist, add it to the beginning since we want newest first
              return [newContentResult, ...prev]
            } else {
              // If it exists, update it in place
              return prev.map(result =>
                result.id === newContentResult.id ? newContentResult : result,
              )
            }
          })
        }
      },
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const deleteResult = async (result) => {
    const response = await fetch(
      `/projects/${project.id}/contents/${result.content_id}/destroy_content_result`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').
            getAttribute('content'),
        },
        body: JSON.stringify({ content_result_id: result.id }),
      })
    if (response.ok) {
      setContentResults(prev => prev.filter(r => r.id !== result.id))
      toast({
        message: 'Result deleted successfully',
        type: 'success',
        duration: 3000,
        gravity: 'top',
        position: 'right',
        close: true,
      })
      setContentResult(null)
      setOpen(false)
    } else {
      toast({
        message: 'Failed to delete result',
        type: 'error',
        duration: 3000,
        gravity: 'top',
        position: 'right',
        close: true,
      })
      setContentResult(null)
      setOpen(false)
    }
  }

  const filteredAgents = useMemo(() =>
    listOfAgents.filter(agent =>
      agents?.some(projAgent => projAgent.name === agent.id),
    ), [listOfAgents, agents])

  const filteredResults = useMemo(() => {
    let results = contentResults

    if (state.statusFilters.length > 0) { // filter by status
      results = results.filter(r => state.statusFilters.includes(r.status))
    }

    if (state.searchQuery.length >= 4) { // filter by search query
      results = results.filter(r => {
        if (typeof r?.result === 'string') {
          return r.result.toLowerCase().
            includes(state.searchQuery.toLowerCase())
        }
        if (typeof r?.result === 'object') {
          return JSON.stringify(r.result).
            toLowerCase().
            includes(state.searchQuery.toLowerCase())
        }
        return false
      })
    }

    if (state.selectedAgents.length > 0) { // filter by agent
      results = results.filter(
        r => state.selectedAgents.some(agent => agent.class === r.agent_type))
    }

    return results
  }, [
    state.statusFilters,
    state.searchQuery,
    state.selectedAgents,
    contentResults])

  const handleTabChange = tab => setState(prev => ({ ...prev, activeTab: tab }))

  const handleAgentToggle = agent => {
    setState(prev => ({
      ...prev,
      selectedAgents: prev.selectedAgents.some(a => a.class === agent.class)
        ? prev.selectedAgents.filter(a => a.class !== agent.class)
        : [...prev.selectedAgents, agent],
    }))
  }

  const handleStatusToggle = status => {
    setState(prev => ({
      ...prev,
      statusFilters: prev.statusFilters.includes(status.id)
        ? prev.statusFilters.filter(id => id !== status.id)
        : [...prev.statusFilters, status.id],
    }))
  }

  const handleSearch = event => setState(prev => ({ ...prev, searchQuery: event.target.value }))
  const clearAgents = () => setState(prev => ({ ...prev, selectedAgents: [] }))
  const clearStatus = () => setState(prev => ({ ...prev, statusFilters: [] }))
  const clearSearch = () => setState(prev => ({ ...prev, searchQuery: '' }))

  const TabButton = ({ id, icon: Icon, label }) => (
    <button
      className={`tab gap-2 transition-all duration-200 
        ${state.activeTab === id
        ? 'tab-active text-primary'
        : 'text-base-content'}`}
      onClick={() => handleTabChange(id)}
    >
      <Icon className="w-5 h-5"
            weight={state.activeTab === id ? 'fill' : 'regular'}/>
      {label} {id === 'results' && `(${contentResults.length})`}
    </button>
  )

  const reRunAnalysis = async (result) => {
    const response = await fetch(
      `/projects/${project.id}/contents/${result.content_id}/redo`, {
        method: 'POST',
        body: JSON.stringify({ content_result_id: result.id }),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').
            getAttribute('content'),
        },
      })
    if (response.ok) {
      toast({
        message: 'Analysis re-run successfully',
        type: 'success',
        duration: 3000,
        gravity: 'top',
        position: 'right',
        close: true,
      })
    } else {
      toast({
        message: 'Analysis re-run failed',
        type: 'error',
        duration: 3000,
        gravity: 'top',
        position: 'right',
        close: true,
      })
    }
  }

  const openContentResultModal = (result) => {
    setContentResult(result)
    setOpen(true)
  }

  const copyResult = (result, format = 'json') => {
    let textToCopy;
    switch (format) {
      case 'yaml':
        textToCopy = YAML.stringify(result);
        break;
      case 'xml':
        textToCopy = convertToXML(result);
        break;
      case 'csv':
        textToCopy = convertToCSV(result);
        break;
      default:
        textToCopy = JSON.stringify(result, null, 2);
    }

    copyToClipboard(textToCopy);
    toast({
      message: `Result copied as ${format.toUpperCase()}`,
      type: 'success',
      duration: 3000,
      gravity: 'top',
      position: 'right',
      close: true,
    });
  };

  const convertToXML = (obj) => {
    const convert = (obj) => {
      if (typeof obj !== 'object' || obj === null) {
        return `<value>${obj}</value>`;
      }
      if (Array.isArray(obj)) {
        return obj.map(item => `<item>${convert(item)}</item>`).join('');
      }
      return Object.entries(obj)
        .map(([key, value]) => `<${key}>${convert(value)}</${key}>`)
        .join('');
    };
    return `<?xml version="1.0" encoding="UTF-8"?>\n<root>${convert(obj)}</root>`;
  };

  const convertToCSV = (obj) => {
    const flatten = (obj, prefix = '') => {
      return Object.entries(obj).reduce((acc, [key, value]) => {
        const newKey = prefix ? `${prefix}.${key}` : key;
        if (typeof value === 'object' && value !== null) {
          return [...acc, ...flatten(value, newKey)];
        }
        return [...acc, [newKey, value]];
      }, []);
    };

    const flattened = flatten(obj);
    const headers = flattened.map(([key]) => key).join(',');
    const values = flattened.map(([_, value]) =>
      typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : value
    ).join(',');

    return `${headers}\n${values}`;
  };

  const CopyButton = ({ result }) => (
    <div className="dropdown dropdown-start">
      <button
        tabIndex={0}
        className="btn btn-outline btn-primary btn-xs tooltip tooltip-bottom"
        data-tip="Copy Result"
      >
        <PhosphorIcons.Copy size={14} weight="bold" />
      </button>
      <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 shadow-lg bg-base-100 rounded-box w-52">
        <li>
          <button onClick={() => copyResult(result, 'json')} className="flex items-center gap-2 text-xs">
            <PhosphorIcons.Code size={14} weight="bold" className="text-primary" />
            <span>Copy as JSON</span>
          </button>
        </li>
        <li>
          <button onClick={() => copyResult(result, 'yaml')} className="flex items-center gap-2 text-xs">
            <PhosphorIcons.FileText size={14} weight="bold" className="text-primary" />
            <span>Copy as YAML</span>
          </button>
        </li>
        <li>
          <button onClick={() => copyResult(result, 'xml')} className="flex items-center gap-2 text-xs">
            <PhosphorIcons.FileHtml size={14} weight="bold" className="text-primary" />
            <span>Copy as XML</span>
          </button>
        </li>
        <li>
          <button onClick={() => copyResult(result, 'csv')} className="flex items-center gap-2 text-xs">
            <PhosphorIcons.Table size={14} weight="bold" className="text-primary" />
            <span>Copy as CSV</span>
          </button>
        </li>
      </ul>
    </div>
  );

  const FilterDropdown = ({
    items,
    selected,
    onToggle,
    onClear,
    label,
    icon = 'agent',
  }) => (
    <div className="dropdown">
      <div className="flex gap-2">
        <label tabIndex={0}
               className={`btn btn-xs hover:brightness-110 transition-all btn-outline ${selected.length
                 ? 'btn-primary'
                 : ''}`}>
          <div className="flex gap-2 items-center whitespace-nowrap">
            <span className="text-sm font-medium">
              {selected.length ? 'Active Filters' : label}
            </span>
            {icon == 'agent' ?
              <PhosphorIcons.OpenAiLogo weight="bold" className="h-4 w-4"/>
              :
              <PhosphorIcons.ChartDonut weight="bold" className="h-4 w-4"/>
            }
          </div>
        </label>
        {selected.length > 0 && (
          <button className="btn btn-xs btn-outline" onClick={onClear}>
            <PhosphorIcons.X weight="bold" className="h-4 w-4"/>
          </button>
        )}
      </div>
      <ul tabIndex={0}
          className="dropdown-content z-50 menu p-1 shadow-lg rounded-lg w-80 max-h-[60vh] overflow-y-auto border border-base-300 mt-1 block bg-blue-50">
        {items.map(item => (
          <FilterItem
            key={item.id}
            item={item}
            isSelected={selected.includes(item.id)}
            onToggle={() => onToggle(item)}
          />
        ))}
      </ul>
    </div>
  )

  const FilterItem = ({ item, isSelected, onToggle }) => (
    <li>
      <label
        className="hover:bg-base-300 active:bg-base-300 transition-colors p-2 cursor-pointer">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            className="checkbox checkbox-primary checkbox-xs"
            checked={isSelected}
            onChange={onToggle}
          />
          <div className={`p-1 rounded-lg ${item.bg_class}`}>
            {PhosphorIcons[item.icon] ?
              React.createElement(PhosphorIcons[item.icon], {
                size: 16,
                className: item.color_class,
              }) :
              <PhosphorIcons.Tag size={16} className={item.color_class}/>
            }
          </div>
          <span className="font-medium text-xs">{item.label}</span>
        </div>
      </label>
    </li>
  )

  const ResultCard = ({ result, agentInfo, resultAgentType }) => (
    <div
      className={`card p-2 border-2 transition-all hover:shadow-md ${
        result.status === 'pending' ? 'border-warning' :
        result.status === 'error' ? 'border-error' :
        result.status === 'success' ? 'border-success' :
        'border-base-300'
      }`}
    >
      <div className="flex flex-col gap-1">
        {/* Header with agent info and status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <div className={`p-1 rounded-lg ${agentInfo?.bg_class || 'bg-base-300'}`}>
              {PhosphorIcons[agentInfo?.icon?.trim() || 'Tag'] ?
                React.createElement(PhosphorIcons[agentInfo?.icon?.trim() || 'Tag'], {
                  size: 18,
                  weight: 'bold',
                  className: agentInfo?.color_class || 'text-base-content',
                }) :
                <PhosphorIcons.Tag size={18} weight="bold"
                                   className={agentInfo?.color_class || 'text-base-content'}/>
              }
            </div>
            <div className="flex flex-col gap-1">
              {/* Agent Information */}
              <div className="flex flex-col">
                <span className="font-medium text-sm">{agentInfo?.label || 'Agent'}</span>
                <span
                  className="text-xs text-base-content/70">{agentInfo?.short_label || 'Agent'}</span>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-1">
                <button
                  onClick={() => reRunAnalysis(result)}
                  className="btn btn-outline btn-secondary btn-xs tooltip tooltip-bottom"
                  data-tip="Re-run Analysis"
                >
                  <PhosphorIcons.ArrowClockwise size={14}/>
                </button>
                <CopyButton result={result?.result} />
                <button
                  onClick={() => openContentResultModal(result)}
                  className="btn btn-outline btn-error btn-xs tooltip tooltip-bottom"
                  data-tip="Delete Result"
                >
                  <PhosphorIcons.Trash size={14}/>
                </button>
              </div>
            </div>
          </div>

          <div
            className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
              result.status === 'pending' ? 'border border-warning' :
                result.status === 'error' ? 'border border-error' :
                  'border border-success'
            }`}>
            {result.status} @ {formatDateTime(result.created_at)}
          </div>
        </div>

        {/* Result content */}
        <div className="bg-blue-100/70 rounded-lg p-2">
	        {resultAgentType == 'LanguageDetectorAgent' && (
		        <LanguageDetectorViewResult result={result?.result}/>
	        )}
          {resultAgentType == 'DocumentClassifierAgent' && (
            <CategoryViewResult result={result?.result}/>
          )}
          {resultAgentType == 'ContentTaggerAgent' && (
            <TagsViewResult result={result?.result}/>
          )}
          {resultAgentType == 'SentimentAnalyzerAgent' && (
            <SentimentViewResult result={result?.result}/>
          )}
          {resultAgentType == 'TextSummarizerAgent' && (
            <TextSummarizerViewResult result={result?.result}/>
          )}
          {resultAgentType == 'AnomalyDetectorAgent' && (
            <AnomalyDetectorViewResult result={result?.result}/>
          )}
          {resultAgentType == 'EntityRecognizerAgent' && (
            <EntityRecognizerViewResult result={result?.result}/>
          )}
          {resultAgentType == 'MetadataExtractorAgent' && (
            <MetadataExtractorViewResult result={result?.result}/>
          )}
          {resultAgentType == 'ActionAnalyzerAgent' && (
            <ActionAnalyzerViewResult result={result?.result}/>
          )}
          {resultAgentType == 'DateExtractorAgent' && (
            <DateExtractorViewResult result={result?.result}/>
          )}
           {resultAgentType == 'PiiDetectorAgent' && (
            <PiiDetectorViewResult result={result?.result}/>
          )}
          {resultAgentType == 'DuplicateDetectorAgent' && (
            <DuplicateDetectorViewResult result={result?.result}/>
          )}
           {resultAgentType == 'SpamAnalyzerAgent' && (
            <SpamAnalyzerViewResult result={result?.result}/>
          )}
          {resultAgentType == 'EmotionAnalyzerAgent' && (
            <EmotionAnalyzerViewResult result={result?.result}/>
          )}
          {resultAgentType == 'KpiAnalyzerAgent' && (
            <KpiAnalyzerViewResult result={result?.result}/>
          )}
          {resultAgentType == 'UrgencyAnalyzerAgent' && (
            <UrgencyAnalyzerViewResult result={result?.result}/>
          )}
          {resultAgentType == 'DeadlineAnalyzerAgent' && (
            <DeadlineAnalyzerViewResult result={result?.result}/>
          )}
           {resultAgentType == 'KeywordOptimizerAgent' && (
            <KeywordOptimizerViewResult result={result?.result}/>
          )}
          {resultAgentType == 'SeoAnalyzerAgent' && (
            <SeoAnalyzerViewResult result={result?.result}/>
          )}
          {resultAgentType == 'ToneAnalyzerAgent' && (
            <ToneAnalyzerViewResult result={result?.result}/>
          )}
           {resultAgentType == 'ReadabilityAnalyzerAgent' && (
            <ReadabilityAnalyzerViewResult result={result?.result}/>
          )}
           {resultAgentType == 'ContentStructureAgent' && (
            <ContentStructureViewResult result={result?.result}/>
          )}
           {resultAgentType == 'BiasDetectorAgent' && (
            <BiasDetectorViewResult result={result?.result}/>
          )}
           {resultAgentType == 'FaqValidatorAgent' && (
            <FaqValidatorViewResult result={result?.result}/>
          )}
          {resultAgentType == 'InvoiceExtractorAgent' && (
            <InvoiceExtractorViewResult result={result?.result}/>
          )}
          {resultAgentType == 'IntentAnalyzerAgent' && (
            <IntentAnalyzerViewResult result={result?.result}/>
          )}
           {resultAgentType == 'CitationAnalyzerAgent' && (
            <CitationAnalyzerViewResult result={result?.result}/>
          )}
           {resultAgentType == 'ContractAnalysisAgent' && (
            <ContractAnalysisViewResult result={result?.result}/>
          )}
          {resultAgentType == 'TransactionAnalyzerAgent' && (
            <TransactionAnalyzerViewResult result={result?.result}/>
          )}
          {resultAgentType == 'LabResultAnalyzerAgent' && (
            <LabResultAnalyzerViewResult result={result?.result}/>
          )}
          {resultAgentType == 'MedicalTermAnalyzerAgent' && (
            <MedicalTermAnalyzerViewResult result={result?.result}/>
          )}
          {resultAgentType == 'ProductCategorizerAgent' && (
            <ProductCategorizerViewResult result={result?.result}/>
          )}
          {resultAgentType == 'JobDescriptionAnalyzerAgent' && (
            <JobDescriptionAnalyzerViewResult result={result?.result}/>
          )}
          {resultAgentType == 'MarketingMessageAnalyzerAgent' && (
            <MarketingMessageAnalyzerViewResult result={result?.result}/>
          )}
          {resultAgentType == 'ExpirationCheckerAgent' && (
            <ExpirationCheckerViewResult result={result?.result}/>
          )}
          {resultAgentType == 'TaxCodeIdentifierAgent' && (
            <TaxCodeIdentifierViewResult result={result?.result}/>
          )}
          {resultAgentType == 'SafetyDataSheetAgent' && (
            <SafetyDataSheetViewResult result={result?.result}/>
          )}
          {resultAgentType == 'EnvironmentalSafetyAgent' && (
            <EnvironmentalSdsSystemResult result={result?.result}/>
          )}
          {resultAgentType == 'FoodSpecificationAgent' && (
            <FoodSpecificationViewResult result={result?.result}/>
          )}
	        {resultAgentType == 'FoodSpecComparisonAgent' && (
            <FoodComparisonViewResult result={result?.result} status={result?.status}/>
          )}
          {/* Evaluator result section */}
          {result.evaluator_result !== null && (
            <div className="mt-4 border-t border-base-300 pt-4">
              <pre className="text-sm whitespace-pre-wrap overflow-x-auto">
                {typeof result.evaluator_result === 'object'
                  ? JSON.stringify(result.evaluator_result, null, 2)
                  : String(result.evaluator_result)}
              </pre>
              <span className="text-sm text-gray-600">
                score: {result.evaluator_result_score}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <div className="card p-1">
      <ModalDeleteContentResult
        open={open}
        onClose={() => setOpen(false)}
        onDelete={deleteResult}
        contentResult={contentResult}
      />
      <div className="flex flex-col gap-1">
        <div className="tabs tabs-bordered">
          <TabButton id="content" icon={PhosphorIcons.Article}
                     label="Content"/>
          <TabButton id="results" icon={PhosphorIcons.ChartLine}
                     label="Results"/>
        </div>

        <div className="transition-all duration-300">
          {state.activeTab === 'content' ? (
            <div className="card p-1 overflow-x-hidden">
              <p
                className="text-base-content text-sm whitespace-pre-wrap break-words">{content.body}</p>
            </div>
          ) : (
            <div
              className="card p-1 flex flex-col gap-1 h-[calc(100vh-10rem)]">
              <div
                className="flex flex-row gap-1 sticky top-0 bg-base-100 z-40 border-b border-blue-500 pb-1">
                <FilterDropdown
                  items={filteredAgents}
                  selected={state.selectedAgents.map(agent => agent.id)}
                  onToggle={handleAgentToggle}
                  onClear={clearAgents}
                  label="Filter by Agent"
                />
                <FilterDropdown
                  items={statusFiltersConfig}
                  selected={state.statusFilters}
                  onToggle={handleStatusToggle}
                  onClear={clearStatus}
                  label="Filter by Status"
                  icon="status"
                />
                <input
                  type="text"
                  placeholder="Search for anything"
                  className="input input-sm input-bordered w-full"
                  value={state.searchQuery}
                  onChange={handleSearch}
                />
                {state.searchQuery.length > 0 && (
                  <button
                    className="btn btn-xs btn-outline btn-ghost tooltip tooltip-bottom"
                    onClick={clearSearch}>
                    <PhosphorIcons.X size={16}/>
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-1 overflow-y-auto">
                {filteredResults.map(result => {
                  const agentInfo = listOfAgents.find(
                    agent => agent.class === result.agent_type)
                  return (
                    <ResultCard
                      agentInfo={agentInfo}
                      resultAgentType={result.agent_type}
                      key={result.id}
                      result={result}
                    />
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AnalysisDashboard
